import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import { gigAdminApi } from '@/api';
import type { IThunkAPI, ICampaignSummary } from '@/store/types';
import type {
  IGigAdmin,
  IGig,
  IGigListRequest,
  IGigDetail,
  IGigHardwareRequirementOption,
  IGigLanguageOption,
  selectedHWR,
  IGigLanguageOptionWithlabels,
  IGigCountriesOption,
  IGigcountryOptionWithlabels,
  IGigCountriesPayload,
} from '@/store/admin/types/types';
import { networkProfileApi } from '@/api/admin/network-profile';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '@/helpers';
import {
  IVocabHardwareRequirementOption,
  IVocabLanguage,
  IVocabLanguageProficiency,
  IVocabLanguageProficiencyArea,
  IVocabProvince,
  IVocabCurrency,
} from '@/store/slices/vocab/types';

import { ICountryForSelects, IAgentPosition } from '@/store/types';
//import { i } from 'vitest/dist/index-ea17aa0c';

interface languageObj {
  [key: number]: string;
}

interface countryObj {
  [key: number]: string;
}

interface provinceObj {
  [key: number]: string;
  countryProvinceLength: {
    [countryId: number]: number;
  };
}

const initialState: IGigAdmin = {
  gigList: [],
  gigPreviewDetail: undefined,
  selectedClient: null,
  gig: null,
  gigHardwareRequirements: {
    hardwareRequirements: [],
    isError: false,
    isSuccess: false,
    isFetching: false,
  },
  gigLanguages: {
    languages: [],
    isError: false,
    isSuccess: false,
    isFetching: false,
  },
  gigCountriesDeatils: {
    countries: [],
    isError: false,
    isSuccess: false,
    isFetching: false,
  },
  networkProfileList: [],
  showForm: false,
  isError: false,
  isSuccess: false,
  isFetching: false,
};

export const getGigList = createAsyncThunk<Array<IGig>, IGigListRequest, IThunkAPI>(
  'get/gig/list',
  async (requestPayload, thunkAPI) => {
    try {
      const response = await gigAdminApi.getGigList(requestPayload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigById = createAsyncThunk<IGig, number, IThunkAPI>('get/gig', async (id: number, thunkAPI) => {
  try {
    const response = await gigAdminApi.getGigById(id);
    return response.data.result;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getGigHardwareRequirements = createAsyncThunk<IGigHardwareRequirementOption[], string, IThunkAPI>(
  'get/gighardwareRequirements',
  async (id: string, thunkAPI) => {
    try {
      const response = await gigAdminApi.getGigHardwareRequirements(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigLanguages = createAsyncThunk<IGigLanguageOption[], string, IThunkAPI>(
  'get/gigLanguages',
  async (id: string, thunkAPI) => {
    try {
      const response = await gigAdminApi.getGigLanguages(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigCountriesDetails = createAsyncThunk<IGigCountriesOption[], string, IThunkAPI>(
  'get/gigCountries',
  async (id: string, thunkAPI) => {
    try {
      const response = await gigAdminApi.getGigCountriesDetails(id);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigDetail = createAsyncThunk<ICampaignSummary, IGigDetail, IThunkAPI>(
  'get/admin/gig/detail',
  async (requestObject, thunkAPI) => {
    try {
      const response = await gigAdminApi.getGigDetail(requestObject.gigId, requestObject.countryId);
      return response.data.result;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const addGig = createAsyncThunk<unknown, IGig, IThunkAPI>('post/gig', async (gig, thunkAPI) => {
  try {
    const response = await gigAdminApi.saveGig(gig);

    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const updateGigDetails = createAsyncThunk<unknown, IGig, IThunkAPI>('post/gigDetails', async (gig, thunkAPI) => {
  try {
    const response = await gigAdminApi.updateGigDetails(gig);

    const data = response?.data || [];
    return data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const updateHardwareRequirements = createAsyncThunk<
  unknown,
  { payload: IGigHardwareRequirementOption[]; gigId: string },
  IThunkAPI
>('put/gigHardwareRequirements', async (gigHardwareRequirements, thunkAPI) => {
  try {
    const { gigId, payload } = gigHardwareRequirements;
    const response = await gigAdminApi.updateHardwareRequirements(gigId, payload);

    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const updateLanguages = createAsyncThunk<unknown, { payload: IGigLanguageOption[]; gigId: string }, IThunkAPI>(
  'put/gigLanguages',
  async (gigLanguages, thunkAPI) => {
    try {
      const { gigId, payload } = gigLanguages;
      const response = await gigAdminApi.updateLanguages(gigId, payload);
      const data = response?.data || [];
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const updateCountries = createAsyncThunk<unknown, { payload: IGigCountriesPayload; gigId: string }, IThunkAPI>(
  'put/gigCountries',
  async (gigLanguages, thunkAPI) => {
    try {
      const { gigId, payload } = gigLanguages;
      const response = await gigAdminApi.updateCountries(gigId, [payload]);

      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getNetworkProfileByClientId = createAsyncThunk<unknown, number, IThunkAPI>(
  'get/gig/networkprofile/client/list',
  async (clientId, thunkAPI) => {
    try {
      const response = await networkProfileApi.getNetworkProfileListByClientId(clientId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const gigAdminSlice = createSlice({
  name: 'gigAdmin',
  initialState,
  reducers: {
    clearGigs(state) {
      state.gigList = [];
    },
    clearGig(state) {
      state.gig = null;
    },

    resetHardware(state) {
      state.gigHardwareRequirements = initialState.gigHardwareRequirements;
    },

    setSelectedClient(state, action) {
      state.selectedClient = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getGigList.fulfilled, (state, action) => {
        state.gigList = action.payload;
        state.isFetching = false;
      })
      .addCase(getGigList.pending, state => {
        state.gigList = [];
        state.isFetching = true;
      })
      .addCase(getGigById.fulfilled, (state, action) => {
        state.gig = action.payload;
      })
      .addCase(getGigDetail.fulfilled, (state, action) => {
        state.gigPreviewDetail = action.payload;
      })
      .addCase(getNetworkProfileByClientId.pending, state => {
        state.networkProfileList = [];
      })
      .addCase(getNetworkProfileByClientId.fulfilled, (state, action) => {
        if (action.payload && action.payload instanceof Array) {
          state.networkProfileList = action.payload;
        } else {
          state.networkProfileList = [];
        }
      })
      .addCase(getGigHardwareRequirements.rejected, state => {
        state.gigHardwareRequirements.isSuccess = false;
        state.gigHardwareRequirements.isError = true;
        state.gigHardwareRequirements.isFetching = false;
      })
      .addCase(getGigHardwareRequirements.pending, state => {
        state.gigHardwareRequirements.isSuccess = false;
        state.gigHardwareRequirements.isError = false;
        state.gigHardwareRequirements.isFetching = true;
      })
      .addCase(getGigHardwareRequirements.fulfilled, (state, action) => {
        if (action.payload && action.payload instanceof Array) {
          state.gigHardwareRequirements.hardwareRequirements = action.payload;
          state.gigHardwareRequirements.isSuccess = true;
          state.gigHardwareRequirements.isError = false;
          state.gigHardwareRequirements.isFetching = false;
        } else {
          state.gigHardwareRequirements.hardwareRequirements = [];
        }
      })
      .addCase(getGigLanguages.rejected, state => {
        state.gigLanguages.isSuccess = false;
        state.gigLanguages.isError = true;
        state.gigLanguages.isFetching = false;
      })
      .addCase(getGigLanguages.pending, state => {
        state.gigLanguages.isSuccess = false;
        state.gigLanguages.isError = false;
        state.gigLanguages.isFetching = true;
      })
      .addCase(getGigLanguages.fulfilled, (state, action) => {
        if (action.payload && action.payload instanceof Array) {
          state.gigLanguages.languages = action.payload;
          state.gigLanguages.isSuccess = true;
          state.gigLanguages.isError = false;
          state.gigLanguages.isFetching = false;
        } else {
          state.gigLanguages.languages = [];
        }
      })
      .addCase(getGigCountriesDetails.rejected, state => {
        state.gigCountriesDeatils.isSuccess = false;
        state.gigCountriesDeatils.isError = true;
        state.gigCountriesDeatils.isFetching = false;
      })
      .addCase(getGigCountriesDetails.pending, state => {
        state.gigCountriesDeatils.isSuccess = false;
        state.gigCountriesDeatils.isError = false;
        state.gigCountriesDeatils.isFetching = true;
      })
      .addCase(getGigCountriesDetails.fulfilled, (state, action) => {
        if (action.payload && action.payload instanceof Array) {
          state.gigCountriesDeatils.countries = action.payload;
          state.gigCountriesDeatils.isSuccess = true;
          state.gigCountriesDeatils.isError = false;
          state.gigCountriesDeatils.isFetching = false;
        } else {
          state.gigCountriesDeatils.countries = [];
          state.gigCountriesDeatils.isSuccess = true;
          state.gigCountriesDeatils.isError = false;
          state.gigCountriesDeatils.isFetching = false;
        }
      })
      .addMatcher(isFulfilledAction, state => {
        state.isSuccess = true;
        state.isError = false;
        state.isFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isError = true;
        state.isSuccess = false;
        state.isFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isFetching = true;
        state.gig = null;
      });
  },
});

// actions
export const { clearGigs, clearGig, resetHardware, setSelectedClient } = gigAdminSlice.actions;

// selectors
export const gigListAll = (state: RootState): IGigAdmin['gigList'] => state.gigsAdmin.gigList;
export const gig = (state: RootState): IGigAdmin['gig'] => state.gigsAdmin.gig;
export const gigPreviewDetail = (state: RootState): ICampaignSummary | undefined => state.gigsAdmin.gigPreviewDetail;
export const networkProfileList = (state: RootState): IGigAdmin['networkProfileList'] =>
  state.gigsAdmin.networkProfileList;
export const hardwareRequirementsFetching = (state: RootState): boolean =>
  state.gigsAdmin.gigHardwareRequirements.isFetching;
export const hardwareRequirements = (state: RootState): IGigHardwareRequirementOption[] => {
  let formatedHardwareRequirementReceived: IGigHardwareRequirementOption[] = [];
  let updatedReceivedSelectedHWR: selectedHWR[] = [];
  let finalSelectedreceivedHWR: IGigHardwareRequirementOption[] = [];
  const hardwareRequirementOptionList = state.vocab.hardwareRequirementOptionList;
  const hardwareRequirementList = state.vocab.hardwareRequirementList;
  const hwRequirements = state?.gigsAdmin?.gigHardwareRequirements?.hardwareRequirements;

  if (hardwareRequirementOptionList.length && hardwareRequirementList.length && hwRequirements.length) {
    if (hardwareRequirementOptionList.length && hardwareRequirementList.length) {
      formatedHardwareRequirementReceived = hardwareRequirementList.map(h => {
        const options: IVocabHardwareRequirementOption[] = [];
        if (hardwareRequirementOptionList.length) {
          hardwareRequirementOptionList.forEach(o => {
            if (o.hardwareRequirementId === h.hardwareRequirementId) {
              options.push({
                label: o?.label || '',
                value: o?.hardwareRequirementOptionId || -1,
                hardwareRequirementId: o?.hardwareRequirementId || -1,
                hardwareRequirementOptionId: o?.hardwareRequirementOptionId || -1,
              });
            }
          });
        }
        return {
          label: h?.label || '',
          required: h?.required || false,
          hardwareRequirementId: h.hardwareRequirementId,
          hardwareRequirementOptionId: -1,
          options: options,
        };
      });
    }
    if (hardwareRequirementOptionList.length && hwRequirements.length) {
      updatedReceivedSelectedHWR = hwRequirements.map(h => {
        const filteredOption = hardwareRequirementOptionList.filter(
          o =>
            o.hardwareRequirementId === h.hardwareRequirementId &&
            o.hardwareRequirementOptionId === h.hardwareRequirementOptionId,
        );
        return {
          ...h,
          valueLabel: filteredOption?.[0]?.label || 'None',
        };
      });
    }

    if (formatedHardwareRequirementReceived.length && updatedReceivedSelectedHWR.length) {
      finalSelectedreceivedHWR = formatedHardwareRequirementReceived.map(hwr => {
        const filteredOption = updatedReceivedSelectedHWR.filter(
          o => o.hardwareRequirementId === hwr.hardwareRequirementId,
        );
        return {
          ...hwr,
          hardwareRequirementOptionId: filteredOption?.[0]?.hardwareRequirementOptionId || -1,
          valueLabel: filteredOption?.[0]?.valueLabel || 'none',
        };
      });
    }
  }

  return finalSelectedreceivedHWR;
};

export const gigSelectedLanguages = (state: RootState): IGigLanguageOptionWithlabels[] => {
  const languageList = state?.vocab?.languageList;
  const languageProficiencyList = state?.vocab?.languageProficiencyList;
  const languageProficiencyAreaList = state?.vocab?.languageProficiencyAreaList;

  const languageListObj: languageObj = {};
  const languageProficiencyListObj: languageObj = {};
  const languageProficiencyAreaListObj: languageObj = {};

  const gigLanguages = state?.gigsAdmin?.gigLanguages?.languages || [];
  let gigLanguagesWithlabels = [];

  languageList.forEach((element: IVocabLanguage) => {
    languageListObj[element.languageId] = element.labelLong;
  });

  languageProficiencyList.forEach((element: IVocabLanguageProficiency) => {
    languageProficiencyListObj[element.proficiencyId] = element.label;
  });

  languageProficiencyAreaList.forEach((element: IVocabLanguageProficiencyArea) => {
    languageProficiencyAreaListObj[element.proficiencyAreaId] = element.label;
  });

  gigLanguagesWithlabels = gigLanguages.map(language => {
    return {
      ...language,
      languageLabel: languageListObj[language.languageId],
      proficiencyLabel: languageProficiencyListObj[language.proficiencyId],
      proficiencyAreaLabel: languageProficiencyAreaListObj[language.proficiencyAreaId],
    };
  });
  return gigLanguagesWithlabels;
};

export const gigSelectedCountries = (
  state: RootState,
  clientID: number,
): { gigCountriesWithlabels: IGigcountryOptionWithlabels[]; provincesListObj: provinceObj } => {
  const provincesList = state?.vocab?.provincesList || [];
  const currencyList = state?.vocab?.currencyList || [];
  const countriesList = state?.utils?.countriesList || [];
  const agentPositions = state?.agentPosition?.agentPositions?.[clientID] || [];

  const provincesListObj: provinceObj = {
    countryProvinceLength: {},
  };
  const currencyListObj: countryObj = {};
  const countriesListObj: countryObj = {};
  const agentPositionsObj: countryObj = {};

  const gigCountries = state?.gigsAdmin?.gigCountriesDeatils?.countries || [];

  let gigCountriesWithlabels = [];

  provincesList.forEach((element: IVocabProvince) => {
    provincesListObj[element.id] = element.provincename;
    if (!provincesListObj.countryProvinceLength) {
      provincesListObj.countryProvinceLength = {};
    } else {
      const cplength = provincesListObj.countryProvinceLength?.[element.countryid] || 0;
      provincesListObj.countryProvinceLength[element.countryid] = element?.isapproved === 1 ? cplength + 1 : cplength;
    }
  });

  currencyList.forEach((element: IVocabCurrency) => {
    currencyListObj[element.currencyid] = element.labellong;
  });

  countriesList.forEach((element: ICountryForSelects) => {
    countriesListObj[element.id] = element.countryname;
  });

  agentPositions.forEach((element: IAgentPosition) => {
    agentPositionsObj[element.agentPositionId] = element.agentPositionName;
  });

  gigCountriesWithlabels = gigCountries.map(country => {
    const provinceDtls = provincesList
      .filter((province: IVocabProvince) => province.countryid === country.id && province?.isapproved === 1)
      .map((province: IVocabProvince) => {
        const provinceId = province.id;
        if (country.provinces.indexOf(provinceId) > -1) {
          return {
            value: provinceId,
            label: provincesListObj[provinceId],
            checked: true,
          };
        } else {
          return {
            value: provinceId,
            label: provincesListObj[provinceId],
            checked: false,
          };
        }
      });

    const allSelected = country.provinces.length >= provincesListObj.countryProvinceLength[country.id];
    return {
      ...country,
      countryLabel: countriesListObj[country.id],
      targetEarningsCurrencyLabel: currencyListObj[country.targetEarningsCurrency],
      incubationJobIdLabel: agentPositionsObj[country.incubationJobId],
      mainJobIdLabel: agentPositionsObj[country.mainJobId],
      toggle: false,
      provinceDtls,
      allSelected,
    };
  });
  return { gigCountriesWithlabels, provincesListObj };
};
export const gigCountriesFetching = (state: RootState): boolean => state.gigsAdmin.gigCountriesDeatils.isFetching;
export default gigAdminSlice.reducer;
